import React, { useRef, useState } from "react";

const MAX_UPLOAD_BYTES = 8 * 1024 * 1024;
const MAX_ORIGINAL_SIDE = 1600;
const CROPPED_SIDE = 512;
const CROP_MIN_PERCENT = 0;
const CROP_MAX_PERCENT = 100;
const CROP_ZOOM_MIN = 1;
const CROP_ZOOM_MAX = 3;
const DEFAULT_PROFILE_CROP = Object.freeze({
  x: 50,
  y: 50,
  zoom: 1,
});

function createEmptyImageCache() {
  return {
    src: "",
    image: null,
    loadingPromise: null,
  };
}

function clampValue(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function toFiniteNumber(value, fallback) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") resolve(reader.result);
      else reject(new Error("Resultado de leitura inválido"));
    };
    reader.onerror = () => reject(reader.error || new Error("Falha ao ler imagem"));
    reader.readAsDataURL(file);
  });
}

function loadImageFromDataUrl(dataUrl) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Falha ao carregar imagem"));
    image.src = dataUrl;
  });
}

function drawToJpegDataUrl({ image, sx, sy, sw, sh, width, height }) {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas 2D indisponível");

  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.fillStyle = "#101820";
  context.fillRect(0, 0, width, height);
  context.drawImage(image, sx, sy, sw, sh, 0, 0, width, height);
  return canvas.toDataURL("image/jpeg", 0.9);
}

function buildSquareCropDataUrl(image, cropSettings = DEFAULT_PROFILE_CROP) {
  const sourceWidth = image.naturalWidth || image.width;
  const sourceHeight = image.naturalHeight || image.height;
  if (!sourceWidth || !sourceHeight) {
    throw new Error("Dimensões da imagem inválidas para recorte");
  }

  const safeX = clampValue(
    toFiniteNumber(cropSettings.x, DEFAULT_PROFILE_CROP.x),
    CROP_MIN_PERCENT,
    CROP_MAX_PERCENT
  );
  const safeY = clampValue(
    toFiniteNumber(cropSettings.y, DEFAULT_PROFILE_CROP.y),
    CROP_MIN_PERCENT,
    CROP_MAX_PERCENT
  );
  const safeZoom = clampValue(
    toFiniteNumber(cropSettings.zoom, DEFAULT_PROFILE_CROP.zoom),
    CROP_ZOOM_MIN,
    CROP_ZOOM_MAX
  );

  const baseCropSize = Math.min(sourceWidth, sourceHeight);
  const cropSize = Math.max(1, baseCropSize / safeZoom);
  const centerX = (safeX / 100) * sourceWidth;
  const centerY = (safeY / 100) * sourceHeight;

  const maxStartX = Math.max(0, sourceWidth - cropSize);
  const maxStartY = Math.max(0, sourceHeight - cropSize);
  const cropX = clampValue(centerX - cropSize / 2, 0, maxStartX);
  const cropY = clampValue(centerY - cropSize / 2, 0, maxStartY);

  return drawToJpegDataUrl({
    image,
    sx: cropX,
    sy: cropY,
    sw: cropSize,
    sh: cropSize,
    width: CROPPED_SIDE,
    height: CROPPED_SIDE,
  });
}

async function buildProfileImages(file) {
  const sourceDataUrl = await readFileAsDataUrl(file);
  const image = await loadImageFromDataUrl(sourceDataUrl);
  const sourceWidth = image.naturalWidth || image.width;
  const sourceHeight = image.naturalHeight || image.height;

  if (!sourceWidth || !sourceHeight) {
    throw new Error("Dimensões da imagem inválidas");
  }

  const longestSide = Math.max(sourceWidth, sourceHeight);
  const originalScale = longestSide > MAX_ORIGINAL_SIDE ? MAX_ORIGINAL_SIDE / longestSide : 1;
  const resizedOriginalWidth = Math.max(1, Math.round(sourceWidth * originalScale));
  const resizedOriginalHeight = Math.max(1, Math.round(sourceHeight * originalScale));
  const originalDataUrl = drawToJpegDataUrl({
    image,
    sx: 0,
    sy: 0,
    sw: sourceWidth,
    sh: sourceHeight,
    width: resizedOriginalWidth,
    height: resizedOriginalHeight,
  });

  const croppedDataUrl = buildSquareCropDataUrl(image, DEFAULT_PROFILE_CROP);

  return {
    profileImageOriginal: originalDataUrl,
    profileImageCropped: croppedDataUrl,
    profileImageName: file.name || "",
    profileCropX: DEFAULT_PROFILE_CROP.x,
    profileCropY: DEFAULT_PROFILE_CROP.y,
    profileCropZoom: DEFAULT_PROFILE_CROP.zoom,
  };
}

export default function IdentityStep({ state, onPatch }) {
  const [photoError, setPhotoError] = useState("");
  const [isProcessingPhoto, setIsProcessingPhoto] = useState(false);
  const cropImageCacheRef = useRef(createEmptyImageCache());
  const cropRequestRef = useRef(0);

  const cropX = clampValue(
    toFiniteNumber(state.profileCropX, DEFAULT_PROFILE_CROP.x),
    CROP_MIN_PERCENT,
    CROP_MAX_PERCENT
  );
  const cropY = clampValue(
    toFiniteNumber(state.profileCropY, DEFAULT_PROFILE_CROP.y),
    CROP_MIN_PERCENT,
    CROP_MAX_PERCENT
  );
  const cropZoom = clampValue(
    toFiniteNumber(state.profileCropZoom, DEFAULT_PROFILE_CROP.zoom),
    CROP_ZOOM_MIN,
    CROP_ZOOM_MAX
  );

  async function getCropSourceImage(dataUrl) {
    const cache = cropImageCacheRef.current;
    if (cache.src === dataUrl && cache.image) return cache.image;
    if (cache.src === dataUrl && cache.loadingPromise) return cache.loadingPromise;

    const loadingPromise = loadImageFromDataUrl(dataUrl)
      .then((image) => {
        cropImageCacheRef.current = {
          src: dataUrl,
          image,
          loadingPromise: null,
        };
        return image;
      })
      .catch((error) => {
        cropImageCacheRef.current = createEmptyImageCache();
        throw error;
      });

    cropImageCacheRef.current = {
      src: dataUrl,
      image: null,
      loadingPromise,
    };

    return loadingPromise;
  }

  async function applyCropPatch(cropPatch) {
    if (!state.profileImageOriginal) return;

    const nextCropX = clampValue(
      toFiniteNumber(cropPatch.profileCropX, cropX),
      CROP_MIN_PERCENT,
      CROP_MAX_PERCENT
    );
    const nextCropY = clampValue(
      toFiniteNumber(cropPatch.profileCropY, cropY),
      CROP_MIN_PERCENT,
      CROP_MAX_PERCENT
    );
    const nextCropZoom = clampValue(
      toFiniteNumber(cropPatch.profileCropZoom, cropZoom),
      CROP_ZOOM_MIN,
      CROP_ZOOM_MAX
    );

    const requestId = ++cropRequestRef.current;

    try {
      const sourceImage = await getCropSourceImage(state.profileImageOriginal);
      if (requestId !== cropRequestRef.current) return;

      const croppedDataUrl = buildSquareCropDataUrl(sourceImage, {
        x: nextCropX,
        y: nextCropY,
        zoom: nextCropZoom,
      });

      setPhotoError("");
      onPatch({
        profileCropX: nextCropX,
        profileCropY: nextCropY,
        profileCropZoom: nextCropZoom,
        profileImageCropped: croppedDataUrl,
      });
    } catch {
      if (requestId !== cropRequestRef.current) return;
      setPhotoError("Não foi possível aplicar o recorte. Tente novamente.");
    }
  }

  async function handlePhotoUpload(event) {
    const input = event.target;
    const file = input.files?.[0];
    input.value = "";
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setPhotoError("Selecione um arquivo de imagem válido (PNG, JPG ou WEBP).");
      return;
    }

    if (file.size > MAX_UPLOAD_BYTES) {
      setPhotoError("A imagem excede 8MB. Escolha um arquivo menor.");
      return;
    }

    setPhotoError("");
    setIsProcessingPhoto(true);
    try {
      const images = await buildProfileImages(file);
      cropImageCacheRef.current = createEmptyImageCache();
      cropRequestRef.current += 1;
      onPatch(images);
    } catch {
      setPhotoError("Não foi possível processar a imagem. Tente outra foto.");
    } finally {
      setIsProcessingPhoto(false);
    }
  }

  function clearPhoto() {
    cropImageCacheRef.current = createEmptyImageCache();
    cropRequestRef.current += 1;
    onPatch({
      profileImageOriginal: "",
      profileImageCropped: "",
      profileImageName: "",
      profileCropX: DEFAULT_PROFILE_CROP.x,
      profileCropY: DEFAULT_PROFILE_CROP.y,
      profileCropZoom: DEFAULT_PROFILE_CROP.zoom,
    });
    setPhotoError("");
  }

  function resetCropPosition() {
    void applyCropPatch({
      profileCropX: DEFAULT_PROFILE_CROP.x,
      profileCropY: DEFAULT_PROFILE_CROP.y,
      profileCropZoom: DEFAULT_PROFILE_CROP.zoom,
    });
  }

  const croppedPreview = state.profileImageCropped || state.profileImageOriginal || "";

  return (
    <>
      <div className="field-row">
        <div className="field-group">
          <label htmlFor="charName">Nome *</label>
          <input
            type="text"
            id="charName"
            placeholder="Ex: Aldric"
            maxLength="40"
            autoComplete="off"
            value={state.name}
            onChange={(event) => onPatch({ name: event.target.value })}
          />
        </div>
        <div className="field-group">
          <label htmlFor="charSurname">Sobrenome</label>
          <input
            type="text"
            id="charSurname"
            placeholder="Ex: Forjaferro"
            maxLength="40"
            autoComplete="off"
            value={state.surname}
            onChange={(event) => onPatch({ surname: event.target.value })}
          />
        </div>
      </div>

      <div className="identity-photo-block">
        <div className="identity-photo-header">
          <h3 className="identity-photo-title">Foto do Herói</h3>
          <div className="identity-photo-hint">
            A foto é recortada automaticamente para o avatar e a versão inteira também é guardada para o PDF.
          </div>
        </div>

        <div className="field-group identity-photo-field">
          <label htmlFor="heroProfilePhoto">Upload de imagem</label>
          <input
            type="file"
            id="heroProfilePhoto"
            accept="image/png,image/jpeg,image/webp"
            onChange={handlePhotoUpload}
          />
        </div>

        {isProcessingPhoto ? (
          <div className="identity-photo-chip">Processando imagem...</div>
        ) : null}
        {photoError ? <div className="identity-photo-chip identity-photo-chip--error">{photoError}</div> : null}
        {!state.profileImageOriginal && !isProcessingPhoto && !photoError ? (
          <div className="identity-photo-empty">Nenhuma foto selecionada ainda.</div>
        ) : null}

        {state.profileImageOriginal ? (
          <div className="identity-photo-preview-grid">
            <div className="identity-photo-crop-panel">
              <div className="identity-photo-preview-title">Ajuste do Recorte</div>

              <div className="identity-photo-crop-row">
                <span className="identity-photo-crop-label">Zoom</span>
                <span className="identity-photo-crop-value">{cropZoom.toFixed(2)}x</span>
              </div>
              <input
                type="range"
                min={CROP_ZOOM_MIN}
                max={CROP_ZOOM_MAX}
                step="0.01"
                value={cropZoom}
                className="identity-photo-crop-range"
                onChange={(event) =>
                  void applyCropPatch({ profileCropZoom: Number(event.target.value) })
                }
              />

              <div className="identity-photo-crop-row">
                <span className="identity-photo-crop-label">Horizontal</span>
                <span className="identity-photo-crop-value">{Math.round(cropX)}%</span>
              </div>
              <input
                type="range"
                min={CROP_MIN_PERCENT}
                max={CROP_MAX_PERCENT}
                step="1"
                value={cropX}
                className="identity-photo-crop-range"
                onChange={(event) =>
                  void applyCropPatch({ profileCropX: Number(event.target.value) })
                }
              />

              <div className="identity-photo-crop-row">
                <span className="identity-photo-crop-label">Vertical</span>
                <span className="identity-photo-crop-value">{Math.round(cropY)}%</span>
              </div>
              <input
                type="range"
                min={CROP_MIN_PERCENT}
                max={CROP_MAX_PERCENT}
                step="1"
                value={cropY}
                className="identity-photo-crop-range"
                onChange={(event) =>
                  void applyCropPatch({ profileCropY: Number(event.target.value) })
                }
              />

              <div className="identity-photo-crop-actions">
                <button type="button" className="wiz-btn identity-photo-crop-reset" onClick={resetCropPosition}>
                  Centralizar recorte
                </button>
              </div>
            </div>

            <div className="identity-photo-preview-card">
              <div className="identity-photo-preview-title">Perfil (recorte)</div>
              <img
                className="identity-photo-preview identity-photo-preview--cropped"
                src={croppedPreview}
                alt="Prévia recortada da foto do herói"
                loading="lazy"
              />
            </div>
            <div className="identity-photo-preview-card">
              <div className="identity-photo-preview-title">Foto inteira</div>
              <img
                className="identity-photo-preview"
                src={state.profileImageOriginal}
                alt="Prévia da foto inteira do herói"
                loading="lazy"
              />
            </div>
            <div className="identity-photo-actions">
              <button type="button" className="wiz-btn identity-photo-remove-btn" onClick={clearPhoto}>
                Remover foto
              </button>
              {state.profileImageName ? (
                <span className="identity-photo-file">Arquivo: {state.profileImageName}</span>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}

